import { PubSubClient } from "pubsub-client/client";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
} from "rxjs";
import { odataFuncList, odataFuncOne } from "./odataApi";
import { getUserService } from "./user.service";
import { getClientService } from "./client.service";
import { ApiSchema } from "@api-types";
import { Properties } from "types/object-paths";
import { toastSuccess } from "components/ToastProvider/ToastProvider";

type JobProgress = {
  message: string;
  progress: number;
};
export type ProjectUpdatedEvent = {
  projectId: number;
  updatedBy: number;
  changedPropertiesKeys: Properties<ApiSchema.Project>;
  timestampMs: number;
};

export type TemplateQuestionChangedEvent = {
  questionId: number;
  standardQuestionId?: number | null;
  questionPositionId: number;
};

export type ProjectGroupUpdatedEvent = {
  projectGroupId: number;
  updatedBy: number;
  changedPropertiesKeys: Properties<ApiSchema.ProjectGroup>;
  timestampMs: number;
};

type IncidenceEstimateUpdatedEvent = {
  projectId: number;
  regionId: number;
  incidenceEstimate: number;
};

export type NarrativeGeneratedEvent = {
  projectId: number;
  narrativeId: number;
  narrativeVersionId: number;
};

export type KnowledgeDocumentRetrievalUpdatedEvent = {
  id: number;
  knowledgeDocumentRetrievalStatusId: number;
  timestamp: number;
};

export type KnowledgeDocumentUpdatedEvent = {
  id: number;
  timestamp: number;
};

export type ConversationContentStreamingEvent = {
  content: ApiSchema.AiChatContent;
  order: number;
  threadId: string;
};

export type ConversationContentsAddedEvent = {
  contents: ApiSchema.AiChatContent[];
};

export type ConversationUpdated = {};

export type ConversationStateChangeEvent = {
  streaming: boolean;
};

export type JobError = { error: string; trace?: string };
class WebsocketService {
  private _socketClient$ = new BehaviorSubject<PubSubClient | null>(null);

  public socketClient$ = this._socketClient$.asObservable();
  public reconnected$ = this.socketClient$.pipe(
    filter((c): c is PubSubClient => c !== null),
    switchMap((c) => c.reconnected$),
  );
  public userChannel$ = combineLatest({
    client: this.socketClient$,
    user: getUserService().user$.pipe(filter((user) => !!user)),
  }).pipe(
    filter(({ client }) => client !== null),
    map(({ client, user }) => client!.join(`user-${user.id}`)),
    shareReplay(1),
  );

  private readonly clientChannelFor$ = (clientId: number) =>
    this.socketClient$.pipe(
      filter((c) => !!c),
      switchMap((c) =>
        forkJoin([of(c), odataFuncOne("funcClientsGetChannelInfo", { key: clientId }, { clientId }).$()]),
      ),
      map(([socket, channelInfo]) => {
        return socket.join(channelInfo.name, channelInfo.token);
      }),
    );

  private clientChannel$ = getClientService().defaultClientId$.pipe(
    filter((clientId) => !!clientId),
    switchMap((clientId) => this.clientChannelFor$(clientId)),
    shareReplay(1),
  );

  public jobChannel$ = <T>(channelInfo: ApiSchema.ChannelInfoDto) =>
    this.socketClient$.pipe(
      filter((c) => !!c),
      map((c) => c.join(channelInfo.name, channelInfo.token)),
      switchMap((c) =>
        combineLatest([
          of(c),
          c.messages$<T>("Success").pipe(startWith(null)),
          c.messages$<JobError>("Error").pipe(startWith(null)),
          c.messages$<JobProgress>("Progress").pipe(startWith(null)),
        ]),
      ),
      map(([channel, success, error, progress]) => {
        if (success?.event || error?.event) {
          channel.leave();
        }
        return { success, error, progress };
      }),
    );

  /**
   * Listens to project updated events.
   * WARNING: Includes updates made by the current user so
   * filter to prevent double dipping.
   */
  public projectUpdatedEvents$ = this.clientChannel$.pipe(
    switchMap((c) =>
      combineLatest([c.messages$<ProjectUpdatedEvent>("ProjectUpdated"), getUserService().user$]),
    ),
    map(([e, _]) => e),
    filter((e) => !!e?.event),
  );

  public templateQuestionChangedEvents$ = this.clientChannel$.pipe(
    switchMap((c) =>
      combineLatest([
        c.messages$<TemplateQuestionChangedEvent>("TemplateQuestionChanged"),
        getUserService().user$,
      ]),
    ),
    map(([e, _]) => e),
    filter((e) => !!e?.event),
  );

  public projectGroupUpdatedEvents$ = this.clientChannel$.pipe(
    switchMap((c) =>
      combineLatest([c.messages$<ProjectGroupUpdatedEvent>("ProjectGroupUpdated"), getUserService().user$]),
    ),
    map(([e, _]) => e),
    filter((e) => !!e?.event),
  );

  public incidenceEstimateUpdatedEvent$ = this.clientChannel$.pipe(
    switchMap((c) => c.messages$<IncidenceEstimateUpdatedEvent>("IncidenceEstimateUpdated")),
    filter((e) => !!e?.event),
  );

  public downloadsReadyEvent$ = this.clientChannel$.pipe(
    switchMap((c) =>
      combineLatest([c.messages$<ApiSchema.UserDataExport>("DownloadReady"), getUserService().user$]),
    ),
    map(([e, _]) => e),
    filter((e) => !!e?.event),
  );

  public narrativeGeneratedEvent$ = this.clientChannel$.pipe(
    switchMap((c) => c.messages$<NarrativeGeneratedEvent>("NarrativeGenerated")),
    filter((e) => !!e?.event),
  );

  // Joins the given client's channel explicitly (rather than clientChannel$, which is fixed to
  // the default client) so super-admins get events for whichever client they are browsing.
  public knowledgeDocumentRetrievalUpdatedEvents$ = (clientId: number) =>
    this.clientChannelFor$(clientId).pipe(
      switchMap((c) =>
        c.messages$<KnowledgeDocumentRetrievalUpdatedEvent>("KnowledgeDocumentRetrievalUpdated"),
      ),
      filter((e) => !!e?.event),
    );

  // Fired on document create/change and on ingestion status changes; the payload id is always
  // the knowledge document id.
  public knowledgeDocumentUpdatedEvents$ = (clientId: number) =>
    this.clientChannelFor$(clientId).pipe(
      switchMap((c) => c.messages$<KnowledgeDocumentUpdatedEvent>("KnowledgeDocumentUpdated")),
      filter((e) => !!e?.event),
    );

  public notificationMessages$ = this.userChannel$.pipe(
    filter((u) => !!u),
    distinctUntilChanged(),
    switchMap((u) =>
      u.messages$<{ notificationId: number }>(
        "SurveySubmittedForApproval",
        "SurveyApproved",
        "SurveyApprovalDeclined",
        "ProjectSentOutForSurvey",
        "ProjectReady",
        "PasswordReset",
        "UserAddedToTeam",
        "CustomPanelProjectReadyForSurvey",
      ),
    ),
    distinctUntilChanged(),
    filter((e) => !!e?.event),
    shareReplay(1),
  );

  public websocketConnection$ = odataFuncList("funcUsersWebsocketConnection")
    .$()
    .pipe(
      map((w) => w.value as never as string),
      shareReplay(1),
    );

  constructor() {}

  public init() {
    if (window.isUnderTest) {
      return;
    }

    combineLatest([
      this.websocketConnection$.pipe(filter((c) => !!c)),
      getUserService().user$.pipe(filter((user) => !!user)),
    ])
      .pipe(catchError(() => of([null, null])))
      .subscribe(([conn, user]) => {
        if (!conn || !user) return;
        const uri = new URL(conn);
        if (this._socketClient$.value) {
          this._socketClient$.value.disconnect();
        }
        const client = new PubSubClient({
          host: conn,
          appId: uri.searchParams.get("appId")!,
        });
        this._socketClient$.next(client);
      });

    this.downloadsReadyEvent$.subscribe(() => {
      toastSuccess("Download ready", "Your download is ready and has been emailed to you.");
    });
  }
}

let websocketService: WebsocketService | null = null;

export const getWebsocketService = () => {
  if (!websocketService) {
    websocketService = new WebsocketService();
  }
  return websocketService;
};
